import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Bot, Zap, Activity, Bug } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';
import V2ChatHeader from './V2ChatHeader';
import V2ChatMessages from './V2ChatMessages';
import V2ChatInput from './V2ChatInput';
import V2EventLog from './V2EventLog';
import { useV2Workflow } from './hooks/useV2Workflow';

const WorkflowV2ChatPage = () => {
  const { id: workflowId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Get workflow details from URL params or fetch if needed
  const workflowName = searchParams.get('name') || 'Workflow';
  const initialRunId = searchParams.get('run_id'); // Get run_id from URL params
  const agentId = searchParams.get('agentId'); // Get agentId for back navigation

  // State management
  const [showEventLog, setShowEventLog] = useState(false);

  // V2 Workflow hook - handles all workflow logic (polling mode)
  const {
    messages,
    isLoading,
    runId,
    executionStatus,
    currentStep,
    sendMessage,
    events,
    error,
    resetWorkflow,
    hasActiveWorkflow
  } = useV2Workflow(workflowId, token, initialRunId);

  // Cleanup on page unload
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasActiveWorkflow) {
        console.log('🧹 Page unloading - cleaning up workflow');
        resetWorkflow();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasActiveWorkflow, resetWorkflow]);

  // Handle back navigation - go to agent page if agentId exists, otherwise workflows
  const handleBack = () => {
    if (agentId) {
      navigate(`/agents/${agentId}`);
    } else {
      navigate('/workflows');
    }
  };

  // Handle debug - navigate to debug page with runId
  const handleDebug = () => {
    if (runId) {
      navigate(`/debug?execution=${runId}&type=v2`);
    }
  };

  if (!workflowId || !token) {
    return (
      <MainLayout title="Workflow Chat">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Invalid Workflow
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Unable to load workflow chat.
            </p>
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Back to Workflows
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={`${workflowName} - Chat`}>
      {/* Header */}
      <V2ChatHeader
        workflowName={workflowName}
        executionStatus={executionStatus}
        runId={runId}
        onBack={handleBack}
        onDebug={handleDebug}
        onToggleEventLog={() => setShowEventLog(!showEventLog)}
        showEventLog={showEventLog}
        isAdmin={isAdmin}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden h-[calc(100vh-12rem)]">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto">
            <V2ChatMessages
              key={runId || 'initial'}  // Force remount with fresh state when runId changes
              messages={messages}
              isLoading={isLoading}
              workflowName={workflowName}
              error={error}
              runId={runId}
              workflowId={workflowId}
              token={token}
              sendMessage={sendMessage}
              executionStatus={executionStatus}
              currentStep={currentStep}
              isReopenedSession={!!initialRunId}  // Flag to prevent modal auto-open on reopened threads
              agentId={agentId}  // Pass agentId for cancel redirect
            />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <V2ChatInput
              onSendMessage={sendMessage}
              isLoading={isLoading}
              placeholder={`Send a message to start ${workflowName}...`}
              disabled={!token}
            />
          </div>
        </div>

        {/* Event Log Sidebar (Optional) */}
        {showEventLog && (
          <div className="w-96 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <V2EventLog
              events={events}
              runId={runId}
              onClose={() => setShowEventLog(false)}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default WorkflowV2ChatPage;